import React, { useEffect, useMemo } from 'react';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link } from 'react-router-dom';

import useProductBasicDetails from 'data-hooks/useProductBasicDetails';
import useProductCardOptions from 'data-hooks/useProductCardOptions';
import useProductPrice from 'data-hooks/useProductPrice';
import CtaButton from 'components/essentials/Cta/CtaButton';
import { getConstructedImagesForMaterial } from 'data-hooks/useProductCardMaterialPhotos/utils';
import useProductAvailabilityByStock from 'components/consumer/product-card/hooks/useProductAvailabilityByStock';
import removeNull from 'commons/removeNull';
import JBImage from 'components/essentials/JBImage';
import Currency from 'components/consumer/Currency';
import { COMPONENT_CALLER_TYPE } from 'commons/constants';

import { getProductURL, EXCLUDED_OPTION_SCREEN_NAMES } from '../../utils';
import {
  getDefaultOptionsData,
  isOptionValueInOptions,
  getProductOptionsUrl,
} from './utils';

const FINAL_EXCLUDED_OPTIONS = [...EXCLUDED_OPTION_SCREEN_NAMES, 19];

const RecommendationProduct = ({
  product,
  addToCart,
  addingToCart,
  closeCart,
  addingToCartId,
  removeProductIdFromList,
}) => {
  const {
    data: basicDetailsData,
    loading: basicDetailsLoading,
  } = useProductBasicDetails(product.id, null, {
    skipPDPStateHook: true,
  });

  const productId = basicDetailsData?.id;

  const { loading: optionsLoading, data: optionsData } = useProductCardOptions(
    product.id,
    null,
    {
      skipLocalStockHook: false,
      localStockHookOptions: {
        hasStockData: false,
        ssr: false,
      },
    }
  );

  const {
    loading: availabilityLoading,
    isProductOOS,
    isProductDiscontinued,
    isMarkedOOSOrDiscontinued,
    isEFGInternalOOS,
    isEFGInternal,
  } = useProductAvailabilityByStock(product.id, null, {
    allowDiscontinuedRemoval: true,
    hasStockData: false,
  });

  const { data: priceData } = useProductPrice(product.id, null, {
    callerType: COMPONENT_CALLER_TYPE.PRODUCT_CARD,
    layout: 'minimal',
  });

  useEffect(() => {
    if (
      availabilityLoading ||
      !basicDetailsData ||
      typeof removeProductIdFromList !== 'function' ||
      (!isEFGInternal && !isMarkedOOSOrDiscontinued) ||
      (!isEFGInternal && !(isProductDiscontinued || isProductOOS)) ||
      (isEFGInternal && !isEFGInternalOOS)
    ) {
      return;
    }
    removeProductIdFromList(product.id);
  }, [
    availabilityLoading,
    basicDetailsData,
    isEFGInternal,
    isEFGInternalOOS,
    isMarkedOOSOrDiscontinued,
    isProductDiscontinued,
    isProductOOS,
    product,
    removeProductIdFromList,
  ]);

  const isPriceLoaded =
    optionsData?.options?.length &&
    !optionsLoading &&
    !!basicDetailsData &&
    !productId &&
    !!priceData;

  const productData = useMemo(() => {
    if (isPriceLoaded) {
      const { options = [] } = optionsData;
      let hasAllOptions = false;
      const productDetails = {
        price: priceData.price?.final_price,
        fullPrice: priceData.price?.original_price,
        sku: basicDetailsData.sku,
        name: basicDetailsData.name,
        variant: '',
      };
      (product.cartOptions || []).forEach(opt => {
        hasAllOptions = isOptionValueInOptions(
          options,
          opt.optionTypeId,
          opt.value
        );
      });

      return { options, hasAllOptions, productDetails };
    }
    return {};
  }, [
    basicDetailsData?.name,
    basicDetailsData?.sku,
    isPriceLoaded,
    optionsData,
    priceData?.price?.final_price,
    priceData?.price?.original_price,
    product?.cartOptions,
  ]);

  const finalVariation = useMemo(() => {
    if (isPriceLoaded) {
      const { hasAllOptions } = productData;

      if (hasAllOptions) {
        return product.cartSku;
      }
    }
    return null;
  }, [isPriceLoaded, product?.cartSku, productData]);

  const selectedOptions = useMemo(() => {
    if (isPriceLoaded) {
      const { options, hasAllOptions } = productData;
      let finalOptionNames = [];

      if (hasAllOptions) {
        finalOptionNames = product.cartOptions
          .map(opt =>
            !FINAL_EXCLUDED_OPTIONS.includes(opt.optionTypeId)
              ? `${opt.name} ${opt.optionTypeName}`
              : null
          )
          .filter(Boolean);
      } else {
        const { defaultOptionsList } = getDefaultOptionsData(options);
        if (defaultOptionsList?.length) {
          finalOptionNames = defaultOptionsList
            .map(opt =>
              !FINAL_EXCLUDED_OPTIONS.includes(opt.optionTypeId)
                ? `${opt.name} ${opt.optionTypeName}`
                : null
            )
            .filter(Boolean);
        }
      }
      return finalOptionNames;
    }
    return [];
  }, [isPriceLoaded, product?.cartOptions, productData]);

  const productEventDetails = useMemo(() => {
    if (isPriceLoaded) {
      const { options, hasAllOptions, productDetails } = productData;
      if (hasAllOptions) {
        product.cartOptions.forEach(opt => {
          productDetails.variant += `${productDetails.variant ? ', ' : ''}${
            opt.name
          }`;
        });
      } else {
        const { defaultOptionsList } = getDefaultOptionsData(options);
        if (defaultOptionsList?.length) {
          defaultOptionsList.forEach(opt => {
            productDetails.variant += `${productDetails.variant ? ', ' : ''}${
              opt.name
            }`;
          });
        }
      }
      return productDetails;
    }
    return null;
  }, [isPriceLoaded, product?.cartOptions, productData]);

  const productSelection = useMemo(() => {
    if (isPriceLoaded) {
      const { options, hasAllOptions } = productData;
      const selection = {};

      if (hasAllOptions) {
        product.cartOptions.forEach(opt => {
          selection[opt.optionTypeId] = opt.id;
        });
      } else {
        const { defaultOptionsList } = getDefaultOptionsData(options);
        if (defaultOptionsList?.length) {
          defaultOptionsList.forEach(opt => {
            selection[opt.optionTypeId] = opt.id;
          });
        }
      }
      return selection;
    }
    return null;
  }, [isPriceLoaded, product?.cartOptions, productData]);

  const isOptionsLoaded =
    !!basicDetailsData &&
    !!optionsData &&
    !basicDetailsLoading &&
    !optionsLoading;

  const imgData = useMemo(() => {
    if (isOptionsLoaded) {
      const { options = [] } = optionsData;
      const {
        custom_properties: { display_option_images = false },
      } = basicDetailsData;
      const finalOptions = optionsData?.stock?.stockOptions?.length
        ? optionsData.stock.stockOptions
        : options || [];

      const productImageValues = getConstructedImagesForMaterial({
        product: basicDetailsData,
        options: finalOptions,
        layout: 'minimal',
        display_option_images,
        showVariation: finalVariation,
      });

      return { finalOptions, productImageValues };
    }
    return {};
  }, [basicDetailsData, finalVariation, isOptionsLoaded, optionsData]);

  const imgSetForVariant = useMemo(() => {
    if (
      !!basicDetailsData &&
      !!optionsData &&
      !basicDetailsLoading &&
      !optionsLoading &&
      imgSetForVariant !== finalVariation
    ) {
      const { productImageValues } = imgData;

      if (productImageValues?.transparent_hero_image) {
        return finalVariation;
      }
    }
    return null;
  }, [
    basicDetailsData,
    basicDetailsLoading,
    finalVariation,
    imgData,
    optionsData,
    optionsLoading,
  ]);

  const imageSrcSet = useMemo(() => {
    if (isOptionsLoaded && !imageSrcSet?.src) {
      const { productImageValues } = imgData;

      const imageSrcSetVal = removeNull({
        src: productImageValues?.transparent_hero_image_ts,
        fallbackSrc: productImageValues?.transparent_config_image_ts,
        fallbackSrc2: productImageValues?.transparent_config_image,
      });

      if (productImageValues?.transparent_hero_image) {
        return imageSrcSetVal;
      }
    }
    return null;
  }, [imgData, isOptionsLoaded]);

  const imageLink = useMemo(() => {
    if (isOptionsLoaded) {
      const { finalOptions, productImageValues } = imgData;

      const productUrlKey = get(basicDetailsData, 'attributes_min.urlKey', '');
      const productLinkWithOptions = !finalVariation
        ? getProductURL({
            productUrlKey,
            options: finalOptions,
            pickLater: product.cartPickLater,
          })
        : getProductOptionsUrl({
            options: product.cartOptions,
            pickLater: product.cartPickLater,
            productUrlKey,
          });

      if (productImageValues?.transparent_hero_image) {
        return productLinkWithOptions;
      }
    }
    return undefined;
  }, [
    basicDetailsData,
    finalVariation,
    imgData,
    isOptionsLoaded,
    product?.cartOptions,
    product?.cartPickLater,
  ]);

  if (!product.id) return <div />;

  return (
    <div
      data-xc="cart-product"
      className="w-full border-b border-solid border-gray-light7 py-8 px-0 md:py-5 md:px-3 m-0 md:m-[unset] last:border-b-0 hover:bg-brand-light"
    >
      <div className="p-0">
        <div className="flex flex-wrap flex-row items-stretch relative last:border-b-0">
          <div className="basis-[33.33333%] min-w-0">
            <div
              className={cx(
                "mr-6 rounded flex justify-center items-center border border-solid border-gray-light8 bg-gray-light7 w-24 sm:w-28 md:w-[unset] md:p-2 after:content-[''] after:block after:pb-[100%]",
                {
                  'scale-x-[-1]': false,
                }
              )}
            >
              {!isEmpty(imageSrcSet) ? (
                imageLink ? (
                  <Link to={imageLink} onClick={closeCart}>
                    <JBImage
                      lazy={false}
                      alt=""
                      height={80}
                      {...imageSrcSet}
                      className="object-contain"
                    />
                  </Link>
                ) : (
                  <JBImage
                    lazy={false}
                    alt=""
                    height={80}
                    {...imageSrcSet}
                    className="object-contain"
                  />
                )
              ) : null}
            </div>
          </div>
          <div className="basis-[41.66667%] min-w-0">
            <h2 className="m-0 text-base font-bold text-gray leading-none">
              {basicDetailsData?.name}
            </h2>

            <div className="[&_span]:text-gray text-gray-light3 text-xs flex flex-col my-2 mx-0">
              {selectedOptions?.map(optName => (
                <div
                  key={optName}
                  className="text-xs flex items-center relative py-0 text-gray"
                >
                  {optName}
                </div>
              ))}
            </div>

            <div className="w-16 text-base text-gray" />
            <div className="flex pr-8 last:pr-0" />
          </div>
          <div className="basis-1/4 min-w-0 [&_span]:text-gray text-base text-gray">
            <div className="text-right text-base flex flex-col justify-between h-full">
              <div className="flex flex-col items-center justify-end font-bold text-sm">
                {priceData?.price?.final_price ? (
                  <div className="text-sm font-bold text-gray leading-none">
                    <Currency className="text-gray">
                      {priceData.price.final_price}
                    </Currency>
                  </div>
                ) : null}
                <CtaButton
                  block
                  type="submit"
                  color="brand-light"
                  styling="solid-button"
                  data-xc="add-recommend-button"
                  className="w-auto min-w-[60px] p-[.3rem] mt-4"
                  disabled={addingToCart}
                  onClick={async () => {
                    addToCart(
                      product.id,
                      productSelection,
                      productEventDetails
                    );
                  }}
                  isLoading={addingToCart && addingToCartId === product?.id}
                >
                  <span className="text-brand text-sm font-bold">Add</span>
                </CtaButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

RecommendationProduct.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    cartOptions: PropTypes.array,
    cartSku: PropTypes.string,
    cartPickLater: PropTypes.bool,
    cartPickLaterReason: PropTypes.string,
    cartPickLaterReasonId: PropTypes.number,
    cartPickLaterReasonText: PropTypes.string,
  }),
  closeCart: PropTypes.func,
  addToCart: PropTypes.func,
  addingToCart: PropTypes.bool,
  addingToCartId: PropTypes.number,
  removeProductIdFromList: PropTypes.func,
};

export default RecommendationProduct;
